import { useCallback, useEffect, useRef, useState } from "react";
import { apiGet, unpackList } from "./Client.js";
import { fetchAllPages } from "./allPages.js";
import { claimKeys, isLatest, pollIfVisible } from "./loadVersions.js";
import { POLL_KEYS, RESOURCES } from "./resources.js";
import { canRead } from "../Services/pageAccess.js";

// What the app has loaded from the server (the rows of RESOURCES), kept fresh. `reload(keys)` re-reads just those
// resources; `reload()` re-reads everything that loads with the app plus anything a page has since asked for. While
// `enabled` is false nothing is requested.
//   data     { [key]: rows } — plus the lists derived from one answer: leadRows, leadOriginCounts, activeDepartments
//   loaded   { [key]: true } for every resource that has been answered (or refused to this person), so a page that
//            needs one can wait for it
//   loading  true during a full load, not during a partial one
//   errors   [{ key, status, message }] for the requests that failed
const POLL_MS = 30000;

// Puts one answer into `nextData`. A few answers also feed a derived list so every screen derives it the same way.
function applyPayload(nextData, key, mode, payload) {
  const rows = mode === "list" ? unpackList(payload) : payload;
  if (key === "lmsLeads") {
    nextData.lmsLeads = payload;
    nextData.leadRows = payload?.results || [];
    nextData.leadOriginCounts = payload?.origin_counts || {};
  } else if (key === "departments") {
    nextData.departments = rows;
    // `departments` keeps every row, archived included: showing an employee's (possibly former) department name
    // needs them. Every picker reads this list instead, so "archived means don't offer it" is decided once.
    nextData.activeDepartments = (rows || []).filter((department) => !department.is_archived);
  } else {
    nextData[key] = rows;
  }
}

export function useIntranetData(reloadKey, enabled) {
  const [state, setState] = useState({ data: {}, loaded: {}, loading: true, errors: [] });
  const hasInitiallyLoaded = useRef(false);
  const loadVersion = useRef(0);
  const keyVersion = useRef({});
  const enabledRef = useRef(enabled);
  const firstMount = useRef(true);
  enabledRef.current = enabled;
  // Mirrors state so `load` can read the latest `me` and what is loaded without depending on state and re-creating itself (and the
  // polling timer it feeds) on every render.
  const stateRef = useRef(state);
  stateRef.current = state;

  const load = useCallback(async (keys) => {
    if (!enabledRef.current) { hasInitiallyLoaded.current = false; return; }
    const myVersion = ++loadVersion.current;
    const isPartial = Array.isArray(keys) && keys.length > 0;
    const wanted = isPartial
      ? RESOURCES.filter(({ key }) => keys.includes(key))
      : RESOURCES.filter((r) => r.live || stateRef.current.loaded[r.key]);

    setState((cur) => ({
      data: isPartial || hasInitiallyLoaded.current ? cur.data : {},
      loaded: isPartial || hasInitiallyLoaded.current ? cur.loaded : {},
      loading: isPartial ? cur.loading : true,
      errors: isPartial ? cur.errors : [],
    }));

    // The page-access grid comes with `me`, so the very first load cannot be filtered by it. From then on a request
    // for a page the person cannot read is not sent: it would only come back 403.
    const me = stateRef.current.data.me;
    const toFetch = me ? wanted.filter(({ page }) => !page || canRead(me, page)) : wanted;
    claimKeys(keyVersion.current, toFetch.map(({ key }) => key), myVersion);

    const results = await Promise.allSettled(toFetch.map(({ key, path, mode = "list" }) =>
      fetchAllPages(apiGet, path).then(
        (payload) => ({ key, payload, mode }),
        (err) => { err._key = key; err._mode = mode; throw err; },
      )));

    setState((cur) => {
      const nextData = { ...cur.data };
      const loaded = { ...cur.loaded };
      // A resource this person may not read is not asked for, and counts as answered so nothing waits for it.
      for (const { key } of wanted) if (!toFetch.some((r) => r.key === key)) loaded[key] = true;
      const errors = isPartial ? cur.errors.filter((err) => !keys.includes(err.key)) : [];
      for (const result of results) {
        const answered = result.status === "fulfilled" ? result.value : { key: result.reason?._key, mode: result.reason?._mode };
        // A newer load has asked for this resource since; its answer is the one to keep.
        if (answered.key && !isLatest(keyVersion.current, answered.key, myVersion)) continue;
        if (answered.key) loaded[answered.key] = true;
        if (result.status === "fulfilled") {
          applyPayload(nextData, answered.key, answered.mode, answered.payload);
        } else {
          errors.push({ key: answered.key, status: result.reason?.status, message: result.reason?.message || "Request failed" });
          if (!isPartial && answered.key) nextData[answered.key] = answered.mode === "list" ? [] : null;
        }
      }
      return { data: nextData, loaded, loading: false, errors };
    });
    hasInitiallyLoaded.current = true;
  }, []);

  // The full load: once on mount and again whenever `reloadKey` changes (sign-in, or the Retry button).
  useEffect(() => {
    if (!enabled) return;
    if (firstMount.current) {
      firstMount.current = false;
      hasInitiallyLoaded.current = false;
    }
    load();
  }, [reloadKey, enabled, load]);

  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setInterval(() => pollIfVisible(load, POLL_KEYS), POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, load]);

  // Coming back to the tab re-reads `me`, so a changed grant or a closed session shows at once.
  useEffect(() => {
    if (!enabled) return undefined;
    const onVisible = () => { if (document.visibilityState === "visible") load(["me"]); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [enabled, load]);

  return { ...state, reload: load };
}
